'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useId,
  useRef,
  useState,
} from 'react'

import { Check, ChevronDown, Copy } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { twMerge } from 'tailwind-merge'

import { compactMarkdownComponents, markdownComponents } from '../markdown'

// ============================================================================
// Context
// ============================================================================

interface ContentPanelContextValue {
  contentId: string
  copied: boolean
  handleCopy: () => void
  size: 'default' | 'compact'
  variant: 'highlight' | 'subtle' | 'bordered' | 'inline'
}

const ContentPanelContext = createContext<ContentPanelContextValue | null>(null)

function useContentPanelContext() {
  const context = useContext(ContentPanelContext)
  if (!context) {
    throw new Error(
      'ContentPanelV2.* components must be used within a ContentPanelV2',
    )
  }
  return context
}

// ============================================================================
// Types
// ============================================================================

interface ContentPanelV2Props {
  children: React.ReactNode
  /** Size variant - compact for file cards, default for main content */
  size?: 'default' | 'compact'
  /** Visual style variant. Use 'inline' for no border/background (embedded in cards) */
  variant?: 'highlight' | 'subtle' | 'bordered' | 'inline'
  className?: string
}

interface ContentPanelHeaderProps {
  children: React.ReactNode
  className?: string
}

interface ContentPanelTitleProps {
  children: React.ReactNode
  className?: string
}

interface ContentPanelTitleIconProps {
  children: React.ReactNode
  className?: string
}

interface ContentPanelTitleTextProps {
  children: React.ReactNode
  className?: string
}

interface ContentPanelContentProps {
  children: React.ReactNode
  /** Parse children as markdown (children must be a string) */
  markdown?: boolean
  /** Enable expand/collapse. Set to number of lines to clamp initially (e.g., 3) */
  collapsible?: number
  /** Callback when expanded state changes (only fires when collapsible is enabled) */
  onExpandedChange?: (expanded: boolean) => void
  className?: string
}

interface ContentPanelCopyButtonProps {
  className?: string
}

// ============================================================================
// Utilities
// ============================================================================

/**
 * Copies rich HTML content to clipboard by:
 * 1. Cloning the element
 * 2. Stripping all styling for clean semantic HTML
 * 3. Using Selection API for reliable rich text copy
 */
function copyRichContent(elementId: string): boolean {
  const element = document.getElementById(elementId)
  if (!element) return false

  // Clone and strip styling for clean semantic HTML
  const clone = element.cloneNode(true) as HTMLElement
  const stripStyles = (el: HTMLElement) => {
    el.removeAttribute('class')
    el.removeAttribute('style')
    el.removeAttribute('id')
    Array.from(el.children).forEach((child) =>
      stripStyles(child as HTMLElement),
    )
  }
  stripStyles(clone)

  // Create a temporary container for the clean HTML
  const tempContainer = document.createElement('div')
  tempContainer.appendChild(clone)
  tempContainer.style.position = 'absolute'
  tempContainer.style.left = '-9999px'
  document.body.appendChild(tempContainer)

  // Use Selection API for reliable rich copy
  const selection = window.getSelection()
  const range = document.createRange()

  // Select the clean content
  range.selectNodeContents(tempContainer)
  selection?.removeAllRanges()
  selection?.addRange(range)

  // Copy using execCommand (works with rich text)
  let success = false
  try {
    success = document.execCommand('copy')
  } catch {
    success = false
  }

  // Clean up
  selection?.removeAllRanges()
  document.body.removeChild(tempContainer)

  return success
}

// ============================================================================
// Style Variants
// ============================================================================

const sizeStyles = {
  default: {
    container: 'rounded-lg p-4 md:p-5',
    header: 'mb-3',
    icon: 'h-3.5 w-3.5',
    title: 'text-sm',
    content: 'text-sm leading-relaxed',
    copyButton: 'h-6 px-2 text-xs',
    footer: 'mt-3',
  },
  compact: {
    container: 'rounded-md p-3 shadow-sm',
    header: 'mb-1.5',
    icon: 'h-3 w-3',
    title: 'text-sm',
    content: 'text-xs leading-relaxed',
    copyButton: 'h-5 px-1.5 text-xs',
    footer: 'mt-1',
  },
}

const variantStyles = {
  highlight: {
    container:
      'border border-accent-100 bg-white dark:border-accent-800 dark:bg-primary-900',
    title: 'text-accent-600 dark:text-accent-400',
    content: 'text-primary-700 dark:text-primary-300',
  },
  subtle: {
    container:
      'border border-primary-100 bg-primary-50 dark:border-primary-700 dark:bg-primary-800/50',
    title: 'text-accent-600 dark:text-accent-400',
    content: 'text-primary-600 dark:text-primary-300',
  },
  bordered: {
    container:
      'border border-primary-200 bg-white dark:border-primary-700 dark:bg-primary-900',
    title: 'text-accent-600 dark:text-accent-400',
    content: 'text-primary-800 dark:text-primary-200',
  },
  inline: {
    container: '',
    title: 'text-accent-600 dark:text-accent-400',
    content: 'text-primary-600 dark:text-primary-200',
  },
}

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * ContentPanelV2.Header - Container for title and copy button
 */
const Header: React.FC<ContentPanelHeaderProps> = ({ children, className }) => {
  const { size } = useContentPanelContext()
  const styles = sizeStyles[size]

  return (
    <div
      className={twMerge(
        'flex items-center justify-between gap-2',
        styles.header,
        className,
      )}
    >
      {children}
    </div>
  )
}
Header.displayName = 'ContentPanelV2.Header'

/**
 * ContentPanelV2.Title - Compositional container for title icon and text
 *
 * @example
 * ```tsx
 * <ContentPanelV2.Title>
 *   <ContentPanelV2.TitleIcon><FileText /></ContentPanelV2.TitleIcon>
 *   <ContentPanelV2.TitleText>Award Description</ContentPanelV2.TitleText>
 * </ContentPanelV2.Title>
 * ```
 */
const Title: React.FC<ContentPanelTitleProps> = ({ children, className }) => {
  const { variant } = useContentPanelContext()
  const variantClasses = variantStyles[variant]

  return (
    <div
      className={twMerge(
        'flex items-center gap-1.5',
        variantClasses.title,
        className,
      )}
    >
      {children}
    </div>
  )
}
Title.displayName = 'ContentPanelV2.Title'

/**
 * ContentPanelV2.TitleIcon - Icon slot for title
 * Pass any icon component as children
 */
const TitleIcon: React.FC<ContentPanelTitleIconProps> = ({
  children,
  className,
}) => {
  const { size } = useContentPanelContext()
  const sizeClasses = sizeStyles[size]

  return (
    <span
      className={twMerge(
        sizeClasses.icon,
        '[&>svg]:h-full [&>svg]:w-full',
        className,
      )}
    >
      {children}
    </span>
  )
}
TitleIcon.displayName = 'ContentPanelV2.TitleIcon'

/**
 * ContentPanelV2.TitleText - Text slot for title
 */
const TitleText: React.FC<ContentPanelTitleTextProps> = ({
  children,
  className,
}) => {
  const { size } = useContentPanelContext()
  const sizeClasses = sizeStyles[size]

  return (
    <span
      className={twMerge(
        'font-bold uppercase tracking-wider',
        sizeClasses.title,
        className,
      )}
    >
      {children}
    </span>
  )
}
TitleText.displayName = 'ContentPanelV2.TitleText'

/**
 * ContentPanelV2.Content - Main content area (copyable)
 *
 * When markdown=true, children must be a string and will be rendered
 * through ReactMarkdown with the standard markdown components.
 *
 * When collapsible is set to a number, content will be clamped to that
 * many lines with an expand/collapse toggle. The toggle only appears
 * if the content actually overflows.
 */
const Content: React.FC<ContentPanelContentProps> = ({
  children,
  markdown = false,
  collapsible,
  onExpandedChange,
  className,
}) => {
  const { contentId, size, variant } = useContentPanelContext()
  const sizeClasses = sizeStyles[size]
  const variantClasses = variantStyles[variant]
  const [isExpanded, setIsExpanded] = useState(false)
  const [isClamped, setIsClamped] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  // Notify parent when expanded state changes (rerender-dependencies: primitive dep)
  React.useEffect(() => {
    onExpandedChange?.(isExpanded)
  }, [isExpanded, onExpandedChange])

  // Detect if content is actually clamped/overflowing
  React.useEffect(() => {
    const el = contentRef.current
    if (!el || !collapsible) return

    // Check if content overflows (scrollHeight > clientHeight means clamped)
    const checkClamped = () => {
      setIsClamped(el.scrollHeight > el.clientHeight)
    }

    // Check immediately
    checkClamped()

    // Re-check on resize
    const observer = new ResizeObserver(checkClamped)
    observer.observe(el)

    return () => observer.disconnect()
  }, [collapsible, children])

  // Line clamp class based on collapsible value
  const lineClampClass =
    collapsible && !isExpanded
      ? collapsible === 1
        ? 'line-clamp-1'
        : collapsible === 2
          ? 'line-clamp-2'
          : collapsible === 3
            ? 'line-clamp-3'
            : collapsible === 4
              ? 'line-clamp-4'
              : collapsible === 5
                ? 'line-clamp-5'
                : 'line-clamp-6'
      : ''

  // Track if there was a selection when mousedown started
  const hadSelectionOnMouseDown = useRef(false)
  // Debounce collapse to distinguish single click from first click of double-click
  const collapseTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Clean up timeout on unmount
  React.useEffect(() => {
    return () => {
      if (collapseTimeoutRef.current) {
        clearTimeout(collapseTimeoutRef.current)
      }
    }
  }, [])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Stop propagation to prevent parent interactive elements (e.g., NoticesV2.Card button) from reacting
    e.stopPropagation()
    const selection = window.getSelection()
    hadSelectionOnMouseDown.current = !!(
      selection && selection.toString().length > 0
    )
  }, [])

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()

      // When collapsed: any click expands (can't select truncated text anyway)
      if (!isExpanded) {
        if (collapseTimeoutRef.current) {
          clearTimeout(collapseTimeoutRef.current)
          collapseTimeoutRef.current = null
        }
        setIsExpanded(true)
        return
      }

      // When expanded...

      // Double/triple click - cancel pending collapse, allow text selection
      if (e.detail > 1) {
        if (collapseTimeoutRef.current) {
          clearTimeout(collapseTimeoutRef.current)
          collapseTimeoutRef.current = null
        }
        return
      }

      // Single click (detail === 1)
      const selection = window.getSelection()
      const hasSelectionNow = !!(selection && selection.toString().length > 0)

      // User just finished selecting text - don't collapse
      if (hasSelectionNow && !hadSelectionOnMouseDown.current) {
        return
      }

      // There was a pre-existing selection at mousedown - don't collapse
      // (browser may have already cleared it between mousedown and click)
      if (hadSelectionOnMouseDown.current) {
        return
      }

      // Clean single click (no selection at mousedown or now) - schedule collapse
      // This allows double-click to cancel it before it fires
      if (collapseTimeoutRef.current) {
        clearTimeout(collapseTimeoutRef.current)
      }
      collapseTimeoutRef.current = setTimeout(() => {
        collapseTimeoutRef.current = null
        setIsExpanded(false)
      }, 200)
    },
    [isExpanded],
  )

  // Simple toggle for the button (no selection logic needed)
  const handleButtonToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setIsExpanded((prev) => !prev)
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setIsExpanded((prev) => !prev)
    }
  }, [])

  // Only show toggle if content is actually clamped (or was clamped before expanding)
  const showToggle = collapsible && (isClamped || isExpanded)
  const showFade = collapsible && isClamped && !isExpanded
  const isClickable = showToggle

  return (
    <div>
      <div
        ref={contentRef}
        id={contentId}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onMouseDown={isClickable ? handleMouseDown : undefined}
        onClick={isClickable ? handleClick : undefined}
        onKeyDown={isClickable ? handleKeyDown : undefined}
        className={twMerge(
          sizeClasses.content,
          variantClasses.content,
          lineClampClass,
          isClickable && 'cursor-pointer',
          className,
        )}
        style={
          showFade
            ? {
                maskImage:
                  'linear-gradient(to bottom, black 60%, transparent 100%)',
                WebkitMaskImage:
                  'linear-gradient(to bottom, black 60%, transparent 100%)',
              }
            : undefined
        }
      >
        {markdown && typeof children === 'string' ? (
          <ReactMarkdown
            components={
              variant === 'inline'
                ? compactMarkdownComponents
                : markdownComponents
            }
          >
            {children}
          </ReactMarkdown>
        ) : (
          children
        )}
      </div>
      {showToggle && (
        <button
          type="button"
          onClick={handleButtonToggle}
          className="mt-0.5 inline-flex items-center gap-0.5 text-sm font-medium text-accent-600 hover:underline dark:text-accent-400"
        >
          {isExpanded ? 'Show less' : 'Show more'}
          <ChevronDown
            className={twMerge(
              'h-3 w-3 transition-transform',
              isExpanded && 'rotate-180',
            )}
          />
        </button>
      )}
    </div>
  )
}
Content.displayName = 'ContentPanelV2.Content'

/**
 * ContentPanelV2.CopyButton - Copy to clipboard button
 */
const CopyButton: React.FC<ContentPanelCopyButtonProps> = ({ className }) => {
  const { copied, handleCopy, size } = useContentPanelContext()
  const styles = sizeStyles[size]

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={twMerge(
        'inline-flex shrink-0 items-center gap-1 rounded-md border border-transparent text-primary-500 transition-colors',
        'hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700',
        'dark:hover:border-primary-700 dark:hover:bg-primary-800 dark:hover:text-primary-300',
        'active:scale-95',
        styles.copyButton,
        className,
      )}
    >
      {copied ? (
        <>
          <Check className="h-3 w-3" />
          Copied
        </>
      ) : (
        <>
          <Copy className="h-3 w-3" />
          Copy
        </>
      )}
    </button>
  )
}
CopyButton.displayName = 'ContentPanelV2.CopyButton'

/**
 * ContentPanelV2.Footer - Footer area positioned at bottom-right
 *
 * Use this to place the CopyButton at the bottom-right of the panel.
 */
interface ContentPanelFooterProps {
  children: React.ReactNode
  className?: string
}

const Footer: React.FC<ContentPanelFooterProps> = ({ children, className }) => {
  const { size } = useContentPanelContext()
  const styles = sizeStyles[size]

  return (
    <div
      className={twMerge(
        'flex items-center justify-end',
        styles.footer,
        className,
      )}
    >
      {children}
    </div>
  )
}
Footer.displayName = 'ContentPanelV2.Footer'

// ============================================================================
// Main Component
// ============================================================================

/**
 * ContentPanelV2 - Composable content display with copy support
 *
 * A compound component for displaying AI summaries, descriptions, and other
 * content with optional copy-to-clipboard functionality. Supports both default
 * and compact sizes for different contexts.
 *
 * Variants:
 * - highlight: Accent border, white background
 * - subtle: Muted border and background
 * - bordered: Neutral border, white background
 * - inline: **Headless mode** - no container styling at all, just provides
 *   context for copy functionality. Use for embedding in other components.
 *
 * @example Main AI Summary
 * ```tsx
 * <ContentPanelV2 variant="bordered">
 *   <ContentPanelV2.Header>
 *     <ContentPanelV2.Title>
 *       <ContentPanelV2.TitleIcon><Sparkles /></ContentPanelV2.TitleIcon>
 *       <ContentPanelV2.TitleText>Analysis</ContentPanelV2.TitleText>
 *     </ContentPanelV2.Title>
 *   </ContentPanelV2.Header>
 *   <ContentPanelV2.Content markdown>{summary}</ContentPanelV2.Content>
 *   <ContentPanelV2.Footer>
 *     <ContentPanelV2.CopyButton />
 *   </ContentPanelV2.Footer>
 * </ContentPanelV2>
 * ```
 *
 * @example Inline/Headless (embedded in cards, no container styling)
 * ```tsx
 * <ContentPanelV2 size="compact" variant="inline">
 *   <ContentPanelV2.Content markdown collapsible={3}>
 *     {notice.description}
 *   </ContentPanelV2.Content>
 *   <ContentPanelV2.Footer>
 *     <ContentPanelV2.CopyButton />
 *   </ContentPanelV2.Footer>
 * </ContentPanelV2>
 * ```
 */
const ContentPanelV2: React.FC<ContentPanelV2Props> & {
  Header: typeof Header
  Title: typeof Title
  TitleIcon: typeof TitleIcon
  TitleText: typeof TitleText
  Content: typeof Content
  CopyButton: typeof CopyButton
  Footer: typeof Footer
} = ({ children, size = 'default', variant = 'highlight', className }) => {
  const contentId = useId()
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleCopy = useCallback(() => {
    const success = copyRichContent(contentId)
    if (success) {
      setCopied(true)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => setCopied(false), 2000)
    }
  }, [contentId])

  const sizeClasses = sizeStyles[size]
  const variantClasses = variantStyles[variant]

  // For 'inline' variant, skip all container styling (headless mode)
  // Only apply className passed by consumer
  const isHeadless = variant === 'inline'

  return (
    <ContentPanelContext.Provider
      value={{ contentId, copied, handleCopy, size, variant }}
    >
      <div
        className={
          isHeadless
            ? className
            : twMerge(
                sizeClasses.container,
                variantClasses.container,
                className,
              )
        }
      >
        {children}
      </div>
    </ContentPanelContext.Provider>
  )
}

ContentPanelV2.displayName = 'ContentPanelV2'
ContentPanelV2.Header = Header
ContentPanelV2.Title = Title
ContentPanelV2.TitleIcon = TitleIcon
ContentPanelV2.TitleText = TitleText
ContentPanelV2.Content = Content
ContentPanelV2.CopyButton = CopyButton
ContentPanelV2.Footer = Footer

// ============================================================================
// Skeleton Sub-Component
// ============================================================================

interface ContentPanelSkeletonProps {
  /** Size variant matching ContentPanelV2 */
  size?: 'default' | 'compact'
  /** Number of content lines to show */
  lineCount?: number
  /** Whether to show header skeleton */
  showHeader?: boolean
  /** Whether to show footer skeleton */
  showFooter?: boolean
  className?: string
}

/**
 * ContentPanelV2.Skeleton - Loading skeleton for content panels
 *
 * @example
 * ```tsx
 * <ContentPanelV2.Skeleton lineCount={4} showHeader showFooter />
 * ```
 */
const ContentPanelSkeleton: React.FC<ContentPanelSkeletonProps> = ({
  size = 'default',
  lineCount = 4,
  showHeader = true,
  showFooter = true,
  className,
}) => {
  const sizeClasses = sizeStyles[size]
  const variantClasses = variantStyles.bordered

  return (
    <div
      className={twMerge(
        sizeClasses.container,
        variantClasses.container,
        className,
      )}
    >
      {/* Header skeleton */}
      {showHeader && (
        <div
          className={twMerge(
            'flex items-center justify-between gap-2',
            sizeClasses.header,
          )}
        >
          <div className="flex items-center gap-1.5">
            <div
              className={twMerge(
                'animate-pulse rounded bg-primary-200 dark:bg-primary-700',
                sizeClasses.icon,
              )}
            />
            <div
              className={twMerge(
                'animate-pulse rounded bg-primary-200 dark:bg-primary-700',
                size === 'compact' ? 'h-2.5 w-20' : 'h-3 w-32',
              )}
            />
          </div>
        </div>
      )}

      {/* Content lines skeleton - text-sm leading-relaxed ≈ 20-24px per line */}
      <div
        className={twMerge(
          size === 'compact' ? 'space-y-2' : 'space-y-3',
          sizeClasses.content,
        )}
      >
        {[...Array(lineCount)].map((_, i) => (
          <div
            key={i}
            className={twMerge(
              'animate-pulse rounded bg-primary-200 dark:bg-primary-700',
              size === 'compact' ? 'h-3' : 'h-5',
              // Vary widths for natural look
              i === lineCount - 1
                ? 'w-3/4'
                : i % 2 === 0
                  ? 'w-full'
                  : 'w-[92%]',
            )}
          />
        ))}
      </div>

      {/* Footer skeleton */}
      {showFooter && (
        <div
          className={twMerge(
            'flex items-center justify-end',
            sizeClasses.footer,
          )}
        >
          <div
            className={twMerge(
              'animate-pulse rounded-md bg-primary-200 dark:bg-primary-700',
              sizeClasses.copyButton,
              'w-16',
            )}
          />
        </div>
      )}
    </div>
  )
}

ContentPanelSkeleton.displayName = 'ContentPanelV2.Skeleton'

// Attach skeleton to ContentPanelV2
const ContentPanelV2WithSkeleton = ContentPanelV2 as typeof ContentPanelV2 & {
  Skeleton: typeof ContentPanelSkeleton
}
ContentPanelV2WithSkeleton.Skeleton = ContentPanelSkeleton

export { ContentPanelV2WithSkeleton as ContentPanelV2 }
export type {
  ContentPanelV2Props,
  ContentPanelHeaderProps,
  ContentPanelTitleProps,
  ContentPanelTitleIconProps,
  ContentPanelTitleTextProps,
  ContentPanelContentProps,
  ContentPanelCopyButtonProps,
  ContentPanelFooterProps,
  ContentPanelSkeletonProps,
}
